import React from "react";
import { Badge, Box, ScrollArea, Separator, Skeleton, Text } from "@salestrak/ui";

import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import {
  entityFromAssigneeRef,
  formatSla,
  memberStatusBadge,
  resolveEntityLabel,
  stageTypeLabel,
  teamRefToEntity,
  userRefToEntity,
  type EntityData,
} from "#app/lib/entity-visuals";
import { useIssueById } from "#app/lib/hooks/use-issue-by-id";
import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useWorkflowStages } from "#app/lib/hooks/use-workflow-stages";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { formatAge } from "#app/lib/issue-utils";
import { pluralize } from "#app/components/issue-view/activity/activity-utils";

import { RowAction, copyText } from "./row-actions";
import { DetailFieldRow } from "./detail-field-row";
import { EntityVisual } from "./entity-visual";
import { EntityGroupTag } from "./entity-group";
import { EntityTag } from "./entity-tag";
import { statusBadgeVariant } from "./badge-tone";
import type { Omittable } from "@salestrak/ui";

// Hover-card content, mounted only inside an open popover so one lazy hook fires
// per open. Fetching kinds use `cache-first` so pointer travel never sprays
// network.

export interface EntityPreviewProps {
  entity: EntityData;
}

export function EntityPreview({ entity }: EntityPreviewProps) {
  switch (entity.kind) {
    case "user":
      return <UserPreview entity={entity} />;
    case "team":
      return <TeamPreview entity={entity} />;
    case "stage":
      return <StagePreview entity={entity} />;
    case "workflow":
      return <WorkflowPreview entity={entity} />;
    case "issueType":
      return <IssueTypePreview entity={entity} />;
    case "issue":
      return <IssuePreview entity={entity} />;
    case "issueSource":
      return <IssueSourcePreview entity={entity} />;
  }
}

function PreviewFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box padding="sm" gap="xs">
      {children}
    </Box>
  );
}

function PreviewHeader({
  entity,
  title,
  subtitle,
  status,
  visual,
}: Omittable<{
  entity: EntityData;
  title: string;
  subtitle?: string | null;
  status?: React.ReactNode;
  visual?: React.ReactNode;
}>) {
  return (
    <Box direction="row" align="center" gap="sm">
      {visual ?? <EntityVisual entity={entity} size={subtitle ? "md" : "sm"} />}
      <Box shrink flex={1}>
        <Text size="sm" weight="semibold" truncate={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text size="xs" variant="muted" truncate={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {status ? <Box shrink={false}>{status}</Box> : null}
    </Box>
  );
}

function teamUsersToEntities(
  users:
    | ReadonlyArray<{ userId: string; name?: string | null; profileImage?: string | null }>
    | null
    | undefined,
): EntityData[] {
  return (users ?? []).map(userRefToEntity);
}

function FactsSkeleton() {
  return (
    <Box gap="xs">
      <Skeleton className="h-3 w-45" />
      <Skeleton className="h-3 w-30" />
    </Box>
  );
}

function UserPreview({ entity }: { entity: Extract<EntityData, { kind: "user" }> }) {
  const { user, member, loading } = useMemberDetail(entity.id, {
    fetchPolicy: "cache-first",
  });

  const teams = member?.teams ?? [];
  const status = memberStatusBadge(user?.memberStatus ?? entity.user?.memberStatus);
  const initialLoading = loading && !user;

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={entity}
        title={user?.name || resolveEntityLabel(entity)}
        subtitle={user?.title ?? null}
        status={
          <Badge size="sm" variant={statusBadgeVariant(status.color)}>
            {status.label}
          </Badge>
        }
      />
      <Separator />
      {initialLoading ? (
        <FactsSkeleton />
      ) : (
        <Box gap="2xs">
          {user?.email ? (
            <DetailFieldRow compact label="Email" value={user.email} link="email" truncate={1} />
          ) : null}
          {user?.phoneNumber ? (
            <DetailFieldRow compact label="Phone" value={user.phoneNumber} link="phone" />
          ) : null}
          <DetailFieldRow compact label="Teams">
            {teams.length > 0 ? (
              <EntityGroupTag
                entities={teams.map(teamRefToEntity)}
                variant="expanded"
                title="Teams"
              />
            ) : (
              <Text size="xs" variant="muted">
                No teams
              </Text>
            )}
          </DetailFieldRow>
        </Box>
      )}
    </PreviewFrame>
  );
}

function TeamPreview({ entity }: { entity: Extract<EntityData, { kind: "team" }> }) {
  const { team, loading } = useTeamDetail(entity.id, {
    fetchPolicy: "cache-first",
  });

  const leads = team?.leads ?? [];
  const members = team?.members ?? [];
  const initialLoading = loading && !team;

  const leadEntities = teamUsersToEntities(leads);
  const memberEntities = teamUsersToEntities(members);
  const isPrivate = team?.private ?? false;

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={entity}
        title={team?.teamName ?? entity.team.teamName}
        subtitle={team?.teamKey || entity.team.teamKey || null}
        status={
          <Badge size="sm" variant={isPrivate ? "secondary" : "info"}>
            {isPrivate ? "Private" : "Public"}
          </Badge>
        }
      />
      <Separator />
      {initialLoading ? (
        <FactsSkeleton />
      ) : (
        <Box gap="2xs">
          {leadEntities.length > 0 ? (
            <DetailFieldRow compact label={pluralize("Lead", { count: leadEntities.length })}>
              <EntityGroupTag
                entities={leadEntities}
                variant="expanded"
                title="Leads"
              />
            </DetailFieldRow>
          ) : null}
          {memberEntities.length > 0 ? (
            <DetailFieldRow compact
              label="Members"
              trailing={
                <Text size="2xs" variant="muted">
                  {leadEntities.length + memberEntities.length} total
                </Text>
              }
            >
              <EntityGroupTag
                entities={memberEntities}
                variant="stack"
                max={5}
                title="Members"
              />
            </DetailFieldRow>
          ) : null}
          {leadEntities.length === 0 && memberEntities.length === 0 ? (
            <DetailFieldRow compact label="Members">
              <Text size="xs" variant="muted">
                No teammates yet
              </Text>
            </DetailFieldRow>
          ) : null}
        </Box>
      )}
    </PreviewFrame>
  );
}


function StagePreview({ entity }: { entity: Extract<EntityData, { kind: "stage" }> }) {
  const { stageMap } = useWorkspaceData();
  const stage = stageMap.get(entity.id);
  const workflowId = stage?.workflowId ?? entity.workflowId;

  // A stage is a rung in its workflow's ladder — the workflow card focused on
  // this stage says both what the stage is and where the pipeline it sits in.
  if (workflowId) {
    return <WorkflowPreviewCard workflowId={workflowId} focusStageId={entity.id} />;
  }

  // No workflow link resolvable (a bare ref outside workspace data): stage
  // facts alone.
  const typeLabel = stageTypeLabel(stage?.type ?? entity.stage.type);
  const issueCount = stage?.issueCount ?? null;
  const sla = formatSla(stage?.age);

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={entity}
        title={stage?.name ?? resolveEntityLabel(entity)}
        subtitle={typeLabel}
      />
      <Separator />
      <Box gap="2xs">
        {issueCount != null ? (
          <DetailFieldRow compact label="Projects">
            <Text size="xs">
              {issueCount} {pluralize("issue", { count: issueCount })}
            </Text>
          </DetailFieldRow>
        ) : null}
        {sla ? (
          <DetailFieldRow compact label="SLA">
            <Text size="xs">{sla}</Text>
          </DetailFieldRow>
        ) : null}
      </Box>
    </PreviewFrame>
  );
}

function WorkflowPreview({
  entity,
}: {
  entity: Extract<EntityData, { kind: "workflow" }>;
}) {
  return <WorkflowPreviewCard workflowId={entity.id} />;
}

/**
 * The workflow hover card: identity header, then the stage ladder in pipeline
 * order with live per-stage issue counts. `focusStageId` highlights one rung
 * and surfaces its SLA — a stage hover and a workflow hover are the same card,
 * differing only in focus. Mounts inside an open popover, so the stages query
 * (app-default cache-and-network) renders cached rows instantly and refreshes
 * counts in the background on each deliberate hover.
 */
export function WorkflowPreviewCard({
  workflowId,
  focusStageId,
}: Omittable<{
  workflowId: string;
  focusStageId?: string;
}>) {
  const { workflowMap } = useWorkspaceData();
  const { stages, loading } = useWorkflowStages(workflowId);
  const workflow = workflowMap.get(workflowId);
  const initialLoading = loading && stages.length === 0;

  const workflowName = workflow?.name ?? "Workflow";
  const workflowEntity: EntityData = {
    kind: "workflow",
    id: workflowId,
    workflow: { name: workflowName, icon: workflow?.icon ?? null },
  };

  const totalIssues = stages.reduce((sum, s) => sum + (s.issueCount ?? 0), 0);
  const countsLine = initialLoading
    ? null
    : `${stages.length} ${pluralize("stage", { count: stages.length })} · ${totalIssues} ${pluralize("issue", { count: totalIssues })}`;
  const subtitle =
    [workflow?.key, countsLine].filter(Boolean).join(" · ") || null;

  const focusedStage = focusStageId
    ? stages.find((s) => s.stageId === focusStageId)
    : undefined;
  const focusedSla = formatSla(focusedStage?.age);

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={workflowEntity}
        title={workflowName}
        subtitle={subtitle}
        status={
          workflow?.isDefault ? (
            <Badge size="sm" variant="secondary">
              Default
            </Badge>
          ) : undefined
        }
      />
      <Separator />
      {initialLoading ? (
        <FactsSkeleton />
      ) : stages.length === 0 ? (
        <Text size="xs" variant="muted">
          No stages yet
        </Text>
      ) : (
        <>
          <Box paddingY="2xs">
            <Text size="2xs" weight="medium" variant="muted">
              Stages
            </Text>
          </Box>
          <ScrollArea className="max-h-60">
            <Box gap="2xs">
              {stages.map((stage) => {
                const isFocused = stage.stageId === focusStageId;
                return (
                  <Box
                    key={stage.stageId}
                    direction="row"
                    align="center"
                    justify="between"
                    gap="sm"
                    paddingX="xs"
                    paddingY="2xs"
                    rounded="sm"
                    background={isFocused ? "muted" : undefined}
                  >
                    <Box shrink>
                      <EntityTag
                        entity={{
                          kind: "stage",
                          id: stage.stageId,
                          stage,
                          workflowId,
                        }}
                        size="sm"
                        weight={isFocused ? "semibold" : "regular"}
                        interactive={false}
                      />
                    </Box>
                    <Box shrink={false}>
                      <Text size="xs" variant="muted">
                        {stage.issueCount}
                      </Text>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </ScrollArea>
          {focusedStage && focusedSla ? (
            <>
              <Separator />
              <DetailFieldRow compact label="SLA" value={focusedSla} />
            </>
          ) : null}
        </>
      )}
    </PreviewFrame>
  );
}

function IssueTypePreview({
  entity,
}: {
  entity: Extract<EntityData, { kind: "issueType" }>;
}) {
  const { issueTypeMap } = useWorkspaceData();
  const issueType = issueTypeMap.get(entity.id);
  const fieldCount = issueType?.fields?.length ?? 0;

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={entity}
        title={issueType?.name ?? resolveEntityLabel(entity)}
        subtitle={issueType?.description ?? null}
        status={
          issueType?.isDefault ? (
            <Badge size="sm" variant="secondary">
              Default
            </Badge>
          ) : undefined
        }
      />
      <Separator />
      <Box gap="2xs">
        <DetailFieldRow compact label="Fields">
          <Text size="xs">
            {fieldCount} {pluralize("field", { count: fieldCount })}
          </Text>
        </DetailFieldRow>
      </Box>
    </PreviewFrame>
  );
}

function IssueSourcePreview({
  entity,
}: {
  entity: Extract<EntityData, { kind: "issueSource" }>;
}) {
  // Sources carry no related entities to expand — a header is the whole card.
  return (
    <PreviewFrame>
      <PreviewHeader entity={entity} title={resolveEntityLabel(entity)} />
    </PreviewFrame>
  );
}

function IssuePreview({ entity }: { entity: Extract<EntityData, { kind: "issue" }> }) {
  const { stageMap } = useWorkspaceData();
  const { issue, loading } = useIssueById(entity.id);
  const initialLoading = loading && !issue;
  const stage = issue?.stageId ? stageMap.get(issue.stageId) : undefined;
  const ageStatus = issue?.age?.status
    ? AGE_STATUS_CONFIG[issue.age.status as AgeStatus]
    : undefined;
  const ageLabel = formatAge(issue?.age?.date);
  const address = issue?.address?.wholeAddress;
  const latLng = issue?.address?.latLng;
  const addressId = issue?.address?.addressId;

  // Jump to our own map screen, never an external maps provider.
  const appNavigate = useAppNavigate();
  const showOnMap = React.useCallback(() => {
    if (!latLng || !issue?.issueId) return;
    const params = new URLSearchParams({
      lat: String(latLng.lat),
      lng: String(latLng.lng),
      z: "16",
      issueId: issue.issueId,
    });
    if (addressId) params.set("addressId", addressId);
    appNavigate.push(`/map?${params.toString()}`);
  }, [latLng, addressId, issue?.issueId, appNavigate]);

  const assigneeEntities = React.useMemo(
    // Defaulted inside the memo: an `?? []` fallback outside it is a new array
    // every render, and the memo would never hold.
    () => (issue?.assignees ?? []).map(entityFromAssigneeRef),
    [issue?.assignees],
  );

  const statusLine =
    [stage?.name, issue?.issueType?.name].filter(Boolean).join(" · ") || null;

  return (
    <PreviewFrame>
      <PreviewHeader
        entity={entity}
        visual={
          stage ? (
            <EntityVisual
              entity={{ kind: "stage", id: stage.stageId, stage }}
              size="md"
            />
          ) : undefined
        }
        title={issue?.name ?? resolveEntityLabel(entity)}
        subtitle={statusLine}
        status={
          ageLabel && ageStatus ? (
            <Badge size="sm" variant={statusBadgeVariant(ageStatus.background)}>
              {ageLabel} · {ageStatus.label}
            </Badge>
          ) : undefined
        }
      />
      <Separator />
      {initialLoading ? (
        <FactsSkeleton />
      ) : (
        <Box gap="2xs">
          {assigneeEntities.length > 0 ? (
            <DetailFieldRow compact label={pluralize("Assignee", { count: assigneeEntities.length })}>
              <EntityGroupTag
                entities={assigneeEntities}
                variant="expanded"
                title="Assignees"
              />
            </DetailFieldRow>
          ) : null}
          {address ? (
            <DetailFieldRow compact
              label="Location"
              actions={
                <>
                  {latLng ? (
                    <RowAction icon="map-pin" onClick={showOnMap} />
                  ) : null}
                  <RowAction
                    icon="copy"
                    onClick={() => copyText(address, "Address")}
                  />
                </>
              }
            >
              <Text size="xs" truncate={1}>
                {address}
              </Text>
            </DetailFieldRow>
          ) : null}
        </Box>
      )}
    </PreviewFrame>
  );
}
